import { MouseEvent, ReactNode, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";

import { STRONG_DROPDOWN_PAPER_SX } from "components/common/dropdownPaper";

export type ActionsMenuItem = {
  key: string;
  label: string;
  /** Why the row can do this, or why it cannot - a disabled MenuItem shows no tooltip. */
  hint?: string;
  icon: ReactNode;
  separatorBefore?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

/**
 * Everything a task row can do apart from Run, Stop and answering a 2FA prompt.
 * Those stay in the cell because they are what an operator reacts to while a bot
 * waits; the rest reads better as a labelled list than as a row of icons - not
 * least the two removals, which look alike and do very different things.
 */
export default function ActionsMenu({
  actions,
  label,
  ariaLabel = "More task actions",
}: {
  actions: ActionsMenuItem[];
  /** Render a labelled button instead of a bare icon (job rows, where the
   *  menu sits among other labelled controls and an icon reads as an orphan). */
  label?: string;
  ariaLabel?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!actions.length) return null;
  const open = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);
  return (
    <>
      {label ? (
        <Button
          size="small"
          variant="outlined"
          aria-label={ariaLabel}
          endIcon={<MoreVertIcon fontSize="small" />}
          onClick={open}
        >
          {label}
        </Button>
      ) : (
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label={ariaLabel}
          onClick={open}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { elevation: 0, sx: STRONG_DROPDOWN_PAPER_SX } }}
      >
        {actions.map((action, index) => {
          return (
            <Box key={action.key}>
              {action.separatorBefore && index > 0 ? <Divider /> : null}
              <MenuItem
                disabled={action.disabled}
                onClick={() => {
                  setAnchor(null);
                  action.onClick();
                }}
                sx={{ maxWidth: 340, whiteSpace: "normal" }}
              >
                <ListItemIcon sx={action.danger ? { color: "error.main" } : undefined}>
                  {action.icon}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  secondary={action.hint}
                  primaryTypographyProps={
                    action.danger ? { color: "error.main" } : undefined
                  }
                  secondaryTypographyProps={{ sx: { whiteSpace: "normal" } }}
                />
              </MenuItem>
            </Box>
          );
        })}
      </Menu>
    </>
  );
}
